import { type ComponentType, type SVGProps } from "react"

import BiorhythmEmotionalIcon from "@/assets/svg/today/biorhythm/biorhythm_emotional_icon.svg?react"
import BiorhythmIntellectualIcon from "@/assets/svg/today/biorhythm/biorhythm_intellectual_icon.svg?react"
import BiorhythmPhysicalIcon from "@/assets/svg/today/biorhythm/biorhythm_physical_icon.svg?react"

import type { BiorhythmValues } from "../../lib/biorhythm"

const CHART_WIDTH = 104
const CHART_HEIGHT = 74
const ARC_CENTER_X = 52
const ARC_CENTER_Y = 50
const ARC_RADIUS_X = 48
const ARC_RADIUS_Y = 46
const ARC_STROKE_WIDTH = 7
const ARC_MAX_SWEEP_DEGREES = 110
const ARC_TOP_ANGLE = -90
const ARC_DOT_RADIUS = 5
const ARC_DOT_STROKE_WIDTH = 4
const ARC_DOT_STROKE_COLOR = "#0D081C"

interface BiorhythmTodayCardProps {
  label: string
  value: number
  color: string
  backgroundColor: string
  Icon: ComponentType<SVGProps<SVGSVGElement>>
}

const clampBiorhythmValue = (value: number) => Math.max(-100, Math.min(100, value))

const toRadians = (degrees: number) => (degrees * Math.PI) / 180

const getArcPoint = (angleDegrees: number): { x: number; y: number } => {
  const radians = toRadians(angleDegrees)

  return {
    x: ARC_CENTER_X + ARC_RADIUS_X * Math.cos(radians),
    y: ARC_CENTER_Y + ARC_RADIUS_Y * Math.sin(radians),
  }
}

const describeArcPath = (startAngle: number, endAngle: number): string => {
  const start = getArcPoint(startAngle)
  const end = getArcPoint(endAngle)
  const angleDelta = Math.abs(endAngle - startAngle)
  const largeArcFlag = angleDelta > 180 ? 1 : 0
  const sweepFlag = endAngle >= startAngle ? 1 : 0

  return `M ${start.x} ${start.y} A ${ARC_RADIUS_X} ${ARC_RADIUS_Y} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`
}

const TRAIL_PATH = describeArcPath(
  ARC_TOP_ANGLE - ARC_MAX_SWEEP_DEGREES,
  ARC_TOP_ANGLE + ARC_MAX_SWEEP_DEGREES,
)

function BiorhythmArc({ value, color }: Pick<BiorhythmTodayCardProps, "value" | "color">) {
  const normalizedValue = clampBiorhythmValue(value)
  const sweep = (Math.abs(normalizedValue) / 100) * ARC_MAX_SWEEP_DEGREES
  const arcEndAngle = normalizedValue >= 0 ? ARC_TOP_ANGLE + sweep : ARC_TOP_ANGLE - sweep
  const activePath = sweep === 0 ? null : describeArcPath(ARC_TOP_ANGLE, arcEndAngle)

  return (
    <svg
      width={CHART_WIDTH}
      height={CHART_HEIGHT}
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d={TRAIL_PATH}
        stroke="rgba(223, 203, 245, 0.2)"
        strokeWidth={ARC_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {activePath && (
        <path
          d={activePath}
          stroke={color}
          strokeWidth={ARC_STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      <circle
        cx={ARC_CENTER_X}
        cy={ARC_DOT_RADIUS}
        r={ARC_DOT_RADIUS}
        fill={color}
        stroke={ARC_DOT_STROKE_COLOR}
        strokeWidth={ARC_DOT_STROKE_WIDTH}
      />
    </svg>
  )
}

function BiorhythmTodayCard({ label, value, color, backgroundColor, Icon }: BiorhythmTodayCardProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
      <div className="relative h-[74px] w-[104px]">
        <div className="absolute left-0 top-0 h-[74px] w-[104px]">
          <BiorhythmArc value={value} color={color} />
        </div>

        <div className="absolute left-8 top-[26px] flex size-10 items-center justify-center">
          <Icon style={{ width: "40px", height: "40px" }} />
        </div>
      </div>

      <div className="flex w-full flex-col items-center gap-0.5">
        <div
          className="flex w-full items-center justify-center rounded-[13px] px-2 py-1.5 text-center font-body text-[10px] font-medium uppercase leading-none"
          style={{ backgroundColor, color }}
        >
          {label}
        </div>

        <div className="text-center font-display text-[20px] font-medium leading-[25px] text-purple-light">
          {value}%
        </div>
      </div>
    </div>
  )
}

interface BiorhythmForTodayProps {
  values: BiorhythmValues | null
}

/** "Biorhythm for today" cards shown under the main graph. */
export function BiorhythmForToday({ values }: BiorhythmForTodayProps) {
  if (!values) {
    return <div />
  }

  return (
    <div className="mt-10">
      <h2 className="px-4 font-display text-2xl font-medium text-purple-light">Biorhythm for today</h2>

      <div className="mt-4 flex items-start justify-center gap-4 px-4">
        <BiorhythmTodayCard
          Icon={BiorhythmPhysicalIcon}
          label="PHYSICAL"
          value={values.physical}
          color="#9565FD"
          backgroundColor="rgba(149, 101, 253, 0.10)"
        />
        <BiorhythmTodayCard
          Icon={BiorhythmEmotionalIcon}
          label="EMOTIONAL"
          value={values.emotional}
          color="#FF537F"
          backgroundColor="rgba(255, 83, 127, 0.20)"
        />
        <BiorhythmTodayCard
          Icon={BiorhythmIntellectualIcon}
          label="INTELLECTUAL"
          value={values.intellectual}
          color="#50E3C2"
          backgroundColor="rgba(80, 227, 194, 0.20)"
        />
      </div>
    </div>
  )
}
